<template>
  <div class="grow p-3 flex flex-col">
    <div class="flex flex-row flex-nowrap w-full border-b mb-2 relative">
      <FormKit
        v-model="newPresetName"
        label="Nieuw sjabloon"
        :ignore="true"
        type="text"
        placeholder="Naam sjabloon..."
        suffix-icon="add"
        @suffix-icon-click="saveCurrentStateAsPreset"
        @keydown.enter.prevent="saveCurrentStateAsPreset"
      />
    </div>
    <span class="font-bold text-sm">Opgeslagen profielen</span>
    <ul class="list-none">
      <li
        v-for="preset in presets"
        :key="preset.id"
        class="flex flex-row items-center border-2 mt-1 rounded hover:border-primary"
        :class="{ 'border-primary': isEditing(preset) }"
      >
        <div v-if="!isEditing(preset)" class="flex">
          <button
            :id="`preset__${preset.id}`"
            class="p-3 block cursor-pointer grow bg-transparent focus:ring-0 focus:outline-none truncate w-60 text-left"
            @click="applyPreset(preset)"
          >
            {{ preset.name }}
          </button>
          <div class="float-right">
            <AppIcon
              v-tooltip="preset.isOwnPreset ? 'Bewerken' : `Dit profiel is met u gedeeld door ${preset.createdByUser.name} en is daarom niet aanpasbaar.`"
              icon="fa-edit"
              :class="{'text-neutral-400 ml-auto icon !cursor-not-allowed': !preset.isOwnPreset, 'ml-auto hover:text-primary icon active:ring-0 active:outline-none': preset.isOwnPreset}"
              @click="preset.isOwnPreset && editPreset(preset)"
            />
            <AppIcon
              v-tooltip="defaultPreset == preset.id ? 'Verwijderen als standaard' : 'Instellen als standaard'"
              :icon="defaultPreset == preset.id ? 'fa-house-circle-xmark' : 'fa-house'"
              class="ml-auto hover:text-primary icon active:ring-0 active:outline-none"
              @click="setDefaultPreset(preset)"
            />
            <AppIcon
              v-tooltip="preset.isOwnPreset ? 'Verwijderen' : `Dit profiel is met u gedeeld door ${preset.createdByUser.name} en is daarom niet aanpasbaar.`"
              icon="fa-trash"
              :class="{'text-neutral-400 ml-auto icon !cursor-not-allowed': !preset.isOwnPreset, 'ml-auto hover:text-primary icon active:ring-0 active:outline-none': preset.isOwnPreset}"
              @click="preset.isOwnPreset && deletePreset(preset)"
            />
          </div>
        </div>
        <div v-else-if="isEditing(preset)" class="w-full">
          <AppPresetForm @cancel="stopEditing" @onSave="onSave" :preset="preset"/>
        </div>
      </li>
    </ul>
    <p v-if="presets.length == 0">
      Er zijn nog geen profielen voor dit overzicht.
    </p>
  </div>
</template>

<script setup lang="ts">
import {
	UserGridPreset,
	createGridPreset,
	deleteGridPreset,
	getGridPresets,
	setDefaultGridPreset,
} from "@q-food/q-food";
import { IToolPanelParams } from "ag-grid-community";
import { mapValues } from "lodash";
import { utcDate } from "~utils/Date.ts";
import { processFilterModel } from "~utils/Grids/GridProcessFilter.ts";
import { ref } from "vue";
import AppPresetForm from "~components/AppPresetForm.vue";
import { useUserStore } from "~stores/User.ts";

const newPresetName = ref("");
const presets = ref([] as UserGridPreset[]);
const editing = ref<string | null>(null);
const defaultPreset = ref<string>();

const props = defineProps<{
	params: IToolPanelParams & { gridKey: string };
}>();

const { params } = toRefs(props);
const { activeUser } = storeToRefs(useUserStore());

onMounted(async () => {
	try {
		if (params.value.gridKey) {
			presets.value = await getGridPresets(params.value.gridKey);
			applyDefaultPresets();
		}
	} catch (error) {
		//
	}
});

function applyDefaultPresets() {
	for (const preset of presets.value) {
		const presetMap = preset.usersWithDefault.map((it) => it.id);
		if (presetMap.includes(activeUser.value.id)) {
			defaultPreset.value = preset.id;
		}
	}
}

function stopEditing() {
	editing.value = null;
}

async function onSave() {
	editing.value = null;
	if (params.value.gridKey) {
		presets.value = await getGridPresets(params.value.gridKey);
	}
}

function isEditing(preset: UserGridPreset) {
	return preset.id == editing.value;
}

function applyPreset(preset: UserGridPreset) {
	params.value.api.applyColumnState({ state: JSON.parse(preset.columnState), applyOrder: true });
	params.value.api.setFilterModel(processFilterModel(JSON.parse(preset.filterState)));
}

async function editPreset(preset: UserGridPreset) {
	editing.value = preset.id;
}

async function setDefaultPreset(preset: UserGridPreset) {
	await setDefaultGridPreset(preset.id).then(() => {
		defaultPreset.value = preset.id;
	});
}

function getFilterModel(): string {
	const filterModel = mapValues(params.value?.api.getFilterModel(), (filter) => {
		if (utcDate(filter.dateFrom).isSame(utcDate(), "day")) {
			return {
				...filter,
				dateFrom: "$today",
			};
		}

		return filter;
	});

	return JSON.stringify(filterModel);
}

async function deletePreset(preset: UserGridPreset) {
	await deleteGridPreset(preset.id);

	const index = presets.value.indexOf(preset);
	presets.value.splice(index, 1);
}

async function saveCurrentStateAsPreset() {
	if (newPresetName.value.trim() == "") return;

	const newPreset = await createGridPreset({
		gridKey: params.value.gridKey,
		name: newPresetName.value.trim(),
		columnState: JSON.stringify(params.value?.api.getColumnState()),
		filterState: getFilterModel(),
	});
	newPresetName.value = "";
	presets.value.push(newPreset);
}
</script>

<style scoped>
.icon {
  @apply p-3 h-4 w-4 cursor-pointer;
}
</style>
